import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import axios from "axios";
import {Badge, Button, Col, Row, Spinner} from "react-bootstrap";
import {
    setPayrollHourReportDisplayData,
    setPayrollHourReportResponseData
} from "../../../../_reducers/DataPanel/Payroll/payrollSlice";
import {toast} from "react-toastify";
import Input from "../../../common/Input";
import {
    formatEmployeeIdentifier, getAmountWorkingHours,
    getHourIntervals,
    isInPublicHoliday
} from "./Components/constants";
import moment from "moment";
import {
    expandSearch,
    getEmployeeCategoryOptions,
    getKathestosOptions,
    isEmpty, quickClone
} from "../../../../_helpers/commonFunctions";
import BetterCheckBox from "../../../common/BetterCheckBox";
import "./style.css";
import EmployeeInfoModal from "./Components/employee-info-modal";
import Alert from "react-bootstrap/Alert";
import BootstrapDropdown from "react-bootstrap/Dropdown";
import * as XLSX from "xlsx";
import {jsPDF} from "jspdf";
import {autoTable} from "jspdf-autotable";
import {liberationSerif} from "../../../../fonts/liberationSerif";
import {dejavuSans} from "../../../../fonts/DejaVuSans-normal";
import {dejavuSansBold} from "../../../../fonts/DejaVuSans-Bold-bold";
import DateBox from "../../../primer/DateBox";
import DateHelper from "../../../primer/DateHelper/DateHelper";
import Dropdown from "../../../primer/Dropdown";
import {useTranslation} from "../../../../hooks/useTranslation";

const PayrollHourReport = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const company = useSelector((state) => state.COMPANY_DATA.company);
    const companyDetails = useSelector((state) => state.COMPANY_DATA.companyDetails);
    const publicHolidays = useSelector((state) => state.PAYROLL.payrollSettings.publicHolidays);
    const responseData = useSelector((state) => state.PAYROLL.hourReportResponseData);
    const displayData = useSelector((state) => state.PAYROLL.hourReportDisplayData);

    const [loading, setLoading] = useState(false);
    const [updateDisplayData, setUpdateDisplayData] = useState(false);

    // Employee Modal
    const [showEmployeeInfoModal, setShowEmployeeInfoModal] = useState(false);
    const [employeeInfoModalId, setEmployeeInfoModalId] = useState("");

    const hourFromKey = displayData?.dataFrom === "program" ? "hourFrom" : "cardHourFrom";
    const hourToKey = displayData?.dataFrom === "program" ? "hourTo" : "cardHourTo";

    useEffect(() => {
        if (displayData.firstLoad) { // First load
            const month = moment().format("MM");
            const date_from = moment(`15/${month}/${company.year}`, "DD/MM/YYYY").startOf("month").format("DD/MM/YYYY");
            const date_to = moment(`15/${month}/${company.year}`, "DD/MM/YYYY").endOf("month").format("DD/MM/YYYY");
            dispatch(setPayrollHourReportDisplayData({...displayData, firstLoad: false, date_from: date_from, date_to: date_to}))
            fetchReport(false, false);
        }
    }, [displayData.firstLoad])

    useEffect(() => {
        if (updateDisplayData) {
            setUpdateDisplayData(false);
            setDisplayData("auto");
        }
    }, [updateDisplayData])

    const dataFromOptions = [
        {label: "Προγράμματος Εργασίας", value: "program"},
        {label: "Χτυπήματα Καρτών", value: "cardhits"},
    ]

    const fetchReport = (forceNew = false, showToast = true) => {
        setLoading(true);
        let prepReq = { company: company.id }
        if (forceNew) prepReq.forceNew = "true";
        axios.post(`${process.env.REACT_APP_API_URL2}/payroll/fetch-hour-report`, prepReq, {
            headers: { "Content-Type": "application/json" },
        }).then((res) => {
            setLoading(false);
            if (res.data.status === "200") {
                if (showToast) toast.success("Τα δεδομένα ανανεώθηκαν.");
                const prepObj = {
                    data: res.data.data,
                    employees: res.data.employees || [],
                }
                if (res.data.lastUpdate) prepObj.lastUpdate = res.data.lastUpdate;

                dispatch(setPayrollHourReportResponseData({...responseData, ...prepObj}));
                setUpdateDisplayData(true);
            } else {
                if (showToast) toast.error(res.data.message);
                dispatch(setPayrollHourReportResponseData({...responseData, data: [], lastUpdate: null}));
            }
        }).catch((err) => {
            setLoading(false);
            console.log(err);
            if (showToast) toast.error("Σφάλμα κατά την αποστολή αιτήματος.");
        })
    }

    const calculateYearlyOvertime = (data, dataByVat) => {
        const toMonth = moment(displayData.date_to, 'DD/MM/YYYY').month();
        let resData = [];
        for (const vatNum in dataByVat) {
            const rowData = dataByVat[vatNum][0];
            const split = String(rowData.employeeIdentifier).split("|");
            const firstName = split[0];
            const lastName = split[1];
            const matchingEmployeeData = dataByVat[vatNum].filter((el) => {
                const d = moment(el.date, 'DD/MM/YYYY');
                return d.month() <= toMonth && d.year() === company.year;
            });
            let totalOvertime = 0;
            for (const dcData of matchingEmployeeData) {
                const hoursWorked = getHourIntervals(dcData[hourFromKey], dcData[hourToKey]);
                const dayHours = hoursWorked.dayHours;
                const nightHours = hoursWorked.nightHours;
                const isPermit = dcData?.permit === true;

                if (isPermit) continue;

                let overtime = 0;
                const totalHours = +Number(dayHours + nightHours).toFixed(2);
                if (dcData?.workingDaysPerWeek === "5") {
                    if (totalHours > 9) {
                        overtime = totalHours - 9;
                    }
                } else if (dcData?.workingDaysPerWeek === "6") {
                    if (totalHours > 8) {
                        overtime = totalHours - 8;
                    }
                }
                totalOvertime += +Number(overtime).toFixed(2);
            }
            resData.push({
                _id: isEmpty(rowData._id) ? null : rowData._id,
                employee: `${firstName} ${lastName}`,
                totalOvertime: totalOvertime,
                illegalOvertime: totalOvertime > 150 ? totalOvertime - 150 : 0,
            })
        }
        return resData;
    }

    const getSixthDayHours = (data) => {
        const weeks = {};
        for (const entry of data) {
            const mDate = moment(entry.date, "DD/MM/YYYY");
            const weekKey = mDate.isoWeek() + "-" + mDate.isoWeekYear();

            if (!weeks[weekKey]) weeks[weekKey] = [];
            weeks[weekKey].push({ ...entry, moment: mDate });
        }
        let total = 0;
        Object.values(weeks).forEach((days) => {
            const dailyHours = {}; // date : total hours
            const sorted = days.sort((a, b) => a.moment - b.moment);
            for (const day of sorted) {
                const hoursWorked = getHourIntervals(day[hourFromKey], day[hourToKey]);
                const dayHours = hoursWorked.dayHours;
                const nightHours = hoursWorked.nightHours;
                const totalHours = dayHours + nightHours;
                if (dailyHours[day.date]) {
                    dailyHours[day.date] += totalHours;
                } else {
                    dailyHours[day.date] = totalHours;
                }
            }
            const totalDaysWorked = Object.keys(dailyHours).length;
            if (totalDaysWorked > 5) {
                const hoursByDay = Object.values(dailyHours);
                for (let i = 5; i <= totalDaysWorked - 1; i++) {
                    total += hoursByDay[i];
                }
            }
        });
        return +Number(total).toFixed(2);
    }

    const getDailyWorkingHours = (allData, day) => {
        let total = 0;
        const filt = allData.filter((el) => el.date === day);
        for (const dc of filt) {
            total += getAmountWorkingHours(dc.hourFrom, dc.hourTo);
        }
        return +Number(total).toFixed(2);
    }

    const getWeeklyWorkingHours = (allData, aWeekDay) => {
        // aWeekDay in DD/MM/YYYY
        const startOf = moment(aWeekDay, "DD/MM/YYYY").hour(10).isoWeekday(1);
        let total = 0;
        for (let i = 1; i <= 7; i++) {
            const dateStr = startOf.format("DD/MM/YYYY");
            const dcs = allData.filter((el) => el.date === dateStr);
            for (const dc of dcs) {
                total += getAmountWorkingHours(dc.hourFrom, dc.hourTo);
            }
            startOf.add(1, "day");
        }
        return +Number(total).toFixed(2);
    }

    const getWorkingHours = ({
         employeeMonthlyData,
         employeePreviousMonthData,
         employeeCategory,
         kathestos,
         workingDaysPerWeek,
         workingHoursPerWeek = 40,
         employeeDataHistory
    }) => {
        // workingHoursPerWeek used for part-time
        const isCardMode = hourFromKey === "cardHourFrom";
        const workingData = !isCardMode ? employeeMonthlyData : employeeMonthlyData.map((el) => {
            const noProgram = isEmpty(el.hourFrom) || isEmpty(el.hourTo);
            const noCard = isEmpty(el.cardHourFrom) || isEmpty(el.cardHourTo);
            if (noProgram && noCard) return el;
            if (noProgram) return { ...el, hourFrom: el.cardHourFrom, hourTo: el.cardHourTo }; // only card exists
            if (noCard) return null;

            return el;
        }).filter(Boolean);

        const weekData = [...employeePreviousMonthData, ...employeeMonthlyData];

        const resData = {
            totals: {
                //enshma: 0,
                daysWorked: 0,
                hoursWorked: 0,
            },
            working: {
                day: 0,
                night: 0,
                daySunday: 0,
                nightSunday: 0,
                additionalWork: 0,
                publicHolidays: 0,
                sixthDay: workingDaysPerWeek === "5" ? getSixthDayHours(workingData) : 0,
            },
            permits: {
                day: 0,
                night: 0,
                daySunday: 0,
                nightSunday: 0,

                totalDays: 0,
                totalHours: 0,
            },
            overwork: {
                day: 0,
                night: 0,
                daySunday: 0,
                nightSunday: 0,

                totalHours: 0,
            },
            overtime: {
                day: 0,
                night: 0,
                daySunday: 0,
                nightSunday: 0,

                totalHours: 0,
            },
        }
        const uniqueDays = [];
        const uniquePermitDays = [];
        const sundayUniqueDays = [];
        const doneDays = [];
        for (let dcData of workingData) {
            if (doneDays.includes(dcData.date)) continue;

            doneDays.push(dcData.date);
            // Reform kathestos and working hours per week as on how employee was working back then
            let reformedKathestos = kathestos;
            let reformedWorkingHoursPerWeek = workingHoursPerWeek;

            const dayMoment = moment(dcData.date, "DD/MM/YYYY").hour(10);
            const currentData = employeeDataHistory.find((el) =>
                (isEmpty(el.dateTo) && dayMoment > moment(el.dateFrom, "DD/MM/YYYY").hour(9))
                ||
                (!isEmpty(el.dateTo) && dayMoment > moment(el.dateFrom, "DD/MM/YYYY").hour(9)
                    && dayMoment < moment(el.dateTo, "DD/MM/YYYY").hour(11))
            )
            if (currentData) {
                reformedKathestos = currentData.kathestos;
                reformedWorkingHoursPerWeek = currentData.workingHoursPerWeek;
            }

            if (!uniqueDays.includes(dcData.date)) uniqueDays.push(dcData.date);

            const isSunday = dayMoment.day() === 0;
            if (!sundayUniqueDays.includes(dcData.date) && isSunday) sundayUniqueDays.push(dcData.date);

            let dayHours = 0;
            let nightHours = 0;
            const sameDay = workingData.filter((el) => el.date === dcData.date);
            for (const c of sameDay) {
                const hoursWorked = getHourIntervals(c[hourFromKey], c[hourToKey]);
                dayHours += hoursWorked.dayHours;
                nightHours += hoursWorked.nightHours;
            }

            const isPermit = dcData?.permit === true;
            const isPublicHoliday = isInPublicHoliday(publicHolidays, dcData.date);

            if (isPermit) {
                if (!uniquePermitDays.includes(dcData.date)) uniquePermitDays.push(dcData.date);
                if (isSunday) {
                    resData.permits.daySunday += dayHours;
                    resData.permits.nightSunday += nightHours;
                } else {
                    resData.permits.day += dayHours;
                    resData.permits.night += nightHours;
                }
            } else {
                const totalHours = +Number(dayHours + nightHours).toFixed(2);
                resData.totals.hoursWorked += totalHours;
                let additionalWork = 0; // Only for part-time, when working more hours until reaching maximum daily hours
                let overwork = 0;
                let overtime = 0;
                let overworkNight = false;
                let overtimeNight = false;

                // Check for additional work/overwork/overtime
                const workingDaysPerWeek = +dcData?.workingDaysPerWeek || 5;
                const weeklyHours = getWeeklyWorkingHours(weekData, dcData.date);
                const hoursWorkingBasedOnProgram = getDailyWorkingHours(employeeMonthlyData, dcData.date);
                let dailyMaxHours = +Number(reformedWorkingHoursPerWeek / workingDaysPerWeek).toFixed(2);
                if (hoursWorkingBasedOnProgram > 0) dailyMaxHours = hoursWorkingBasedOnProgram;
                if ((!isCardMode && weeklyHours > reformedWorkingHoursPerWeek) || (isCardMode && totalHours > dailyMaxHours)) {
                    if (reformedKathestos === "full-time") {
                        if (dcData?.workingDaysPerWeek === "5") {
                            if (totalHours <= 9 && totalHours > 8) {
                                overwork = totalHours - 8;
                                if (dayHours <= 8) overworkNight = true;
                            } else if (totalHours > 9) {
                                overwork = 1;
                                overtime = totalHours - 9;
                                if (dayHours <= 8) overworkNight = true;
                                if (dayHours <= 9) overtimeNight = true;
                            }
                        } else if (dcData?.workingDaysPerWeek === "6") {
                            if (totalHours > 6.67) {
                                if (totalHours <= 8 && totalHours > 6.67) {
                                    overwork = totalHours - 6.67;
                                    if (dayHours <= 6.67) overworkNight = true;
                                } else if (totalHours > 8) {
                                    overwork = 1.33;
                                    overtime = totalHours - 8;
                                    if (dayHours <= 6.67) overworkNight = true;
                                    if (dayHours <= 8) overtimeNight = true;
                                }
                            }
                        }
                    } else if (reformedKathestos === "part-time") {
                        if (String(dcData?.workingDaysPerWeek) === "5") {
                            if (totalHours > dailyMaxHours && totalHours <= 8) {
                                additionalWork += totalHours - dailyMaxHours;
                            } else if (totalHours > 8 && totalHours <= 9) {
                                additionalWork = 8 - dailyMaxHours;
                                if (weeklyHours > 40) {
                                    overwork = totalHours - 8;
                                    if (dayHours <= 8) overworkNight = true;
                                } else {
                                    additionalWork += totalHours - 8;
                                }
                            } else if (totalHours > 9) {
                                additionalWork = 8 - dailyMaxHours;
                                if (weeklyHours > 40) {
                                    overwork = 1;
                                    overtime = totalHours - 9;
                                    if (dayHours <= 8) overworkNight = true;
                                    if (dayHours <= 9) overtimeNight = true;
                                } else {
                                    additionalWork += 1 + (totalHours - 9);
                                }
                            }
                        } else if (String(dcData?.workingDaysPerWeek) === "6") {
                            if (totalHours > dailyMaxHours && totalHours <= 6.67) {
                                additionalWork += totalHours - dailyMaxHours;
                            } else if (totalHours > 6.67 && totalHours <= 8) {
                                additionalWork = 6.67 - dailyMaxHours;
                                if (weeklyHours > 45) {
                                    overwork = totalHours - 6.67;
                                    if (dayHours <= 6.67) overworkNight = true;
                                } else {
                                    additionalWork += totalHours - 6.67;
                                }
                            } else if (totalHours > 8) {
                                additionalWork = 6.67 - dailyMaxHours;
                                if (weeklyHours > 45) {
                                    overwork = 1.33;
                                    overtime = totalHours - 8;
                                    if (dayHours <= 6.67) overworkNight = true;
                                    if (dayHours <= 8) overtimeNight = true;
                                } else {
                                    additionalWork += 1.33 + (totalHours - 8);
                                }
                            }
                        }
                    }
                    if (isCardMode && weeklyHours <= reformedWorkingHoursPerWeek) {
                        const extraOverProgram = Math.max(0, totalHours - dailyMaxHours);
                        overwork = Math.min(overwork, extraOverProgram);
                        overtime = Math.min(overtime, extraOverProgram - overwork);
                    }
                    additionalWork = +Number(additionalWork).toFixed(2);
                    overwork = +Number(overwork).toFixed(2);
                    overtime = +Number(overtime).toFixed(2);
                }

                if (isSunday) {
                    resData.working.daySunday += dayHours;
                    resData.working.nightSunday += nightHours;

                    if (overwork && !overworkNight) resData.overwork.daySunday += overwork;
                    if (overwork && overworkNight) resData.overwork.nightSunday += overwork;
                    if (overtime && !overtimeNight) resData.overtime.daySunday += overtime;
                    if (overtime && overtimeNight) resData.overtime.nightSunday += overtime;
                } else {
                    resData.working.day += dayHours;
                    resData.working.night += nightHours;

                    if (overwork && !overworkNight) resData.overwork.day += overwork;
                    if (overwork && overworkNight) resData.overwork.night += overwork;
                    if (overtime && !overtimeNight) resData.overtime.day += overtime;
                    if (overtime && overtimeNight) resData.overtime.night += overtime;
                }
                if (additionalWork) resData.working.additionalWork += additionalWork;
                if (overwork) resData.overwork.totalHours += overwork;
                if (overtime) resData.overtime.totalHours += overtime;
                if (isPublicHoliday) resData.working.publicHolidays += dayHours + nightHours;
            }
        }
        resData.working.day = resData.working.day - resData.overwork.day - resData.overtime.day;
        resData.working.night = resData.working.night - resData.overwork.night - resData.overtime.night;
        resData.working.daySunday = resData.working.daySunday - resData.overwork.daySunday - resData.overtime.daySunday;
        resData.working.nightSunday = resData.working.nightSunday - resData.overwork.nightSunday - resData.overtime.nightSunday;

        resData.permits.totalDays = uniquePermitDays.length;
        resData.permits.totalHours = resData.permits.day + resData.permits.night + resData.permits.daySunday + resData.permits.nightSunday;

        // General corrections
        resData.totals.daysWorked = uniqueDays.length;
        if (employeeCategory === "salaried") {
            //resData.totals.enshma = Math.min(Math.ceil(uniqueDays.length * 1.2), 25) + sundayUniqueDays.length;
        } else if (employeeCategory === "daily") {
            //resData.totals.enshma = uniqueDays.length
        } else if (employeeCategory === "hourly") {

        } else if (employeeCategory === "professor") { // ?????

        }

        for (let majorKey in resData) {
            for (let innerKey in resData[majorKey]) {
                resData[majorKey][innerKey] = +Number(resData[majorKey][innerKey]).toFixed(2);
            }
        }

        return resData;
    }

    const filterBySelectedMonth = (data) => {
        const from = moment(displayData.date_from, "DD/MM/YYYY");
        const to = moment(displayData.date_to, "DD/MM/YYYY");
        return data.filter((el) => {
            const d = moment(el.date, "DD/MM/YYYY");
            return d >= from && d <= to;
        });
    }

    const filterByLastSelectedMonth = (data) => {
        const from = moment(displayData.date_from, "DD/MM/YYYY").subtract(1, "month");
        const to = moment(displayData.date_to, "DD/MM/YYYY").subtract(1, "month");
        return data.filter((el) => {
            const d = moment(el.date, "DD/MM/YYYY");
            return d >= from && d <= to;
        });
    }

    const setDisplayData = (dayAnalysis) => {
        setLoading(true);
        if (dayAnalysis === "auto") dayAnalysis = displayData.dayAnalysis;

        const unfilteredData = filterBySelectedMonth(responseData.data);

        const employeeRegex = expandSearch(displayData.employeeFilter);
        const filteredData = quickClone(unfilteredData.filter((el) => employeeRegex.test(el.employeeIdentifier)));

        if (dayAnalysis) {
            for (const d of filteredData) {
                d.hours = getAmountWorkingHours(d[hourFromKey], d[hourToKey]);
            }
            dispatch(setPayrollHourReportDisplayData({
                ...displayData,
                page: 1,
                data: filteredData,
                unfilteredData: unfilteredData,
                totalPages: Math.ceil(filteredData.length / 31),
                dayAnalysis: dayAnalysis
            }));
        } else {
            const dataByVat = {};
            for (const el of responseData.data) {
                const key = String(el.vatNumber);
                (dataByVat[key] ||= []).push(el);
            }
            const yearlyOvertime = calculateYearlyOvertime(responseData.data, dataByVat);

            const employeesDone = new Set();
            let resData = [];
            for (let rowData of unfilteredData) {
                const split = String(rowData.employeeIdentifier).split("|");
                const firstName = split[0];
                const lastName = split[1];
                const vatNum = split[2];
                if (vatNum) {
                    if (employeesDone.has(vatNum)) continue;
                    employeesDone.add(vatNum)
                    const findMatchingMonthlyData = filteredData.filter((el) => String(el.vatNumber) === String(vatNum));
                    const employeePreviousMonthData = filterByLastSelectedMonth(dataByVat[String(vatNum)] || []);
                    const employeeDataHistory = responseData.employees?.find((el) => el._id === rowData._id)?.employeeDataHistory || [];
                    resData.push({
                        _id: isEmpty(rowData._id) ? null : rowData._id,
                        no: String(resData.length + 1),
                        employee: `${firstName} ${lastName} | ${vatNum}`,
                        employeeType: "",
                        salary: 0,
                        employeeCategory: rowData.employeeCategory,
                        kathestos: rowData.kathestos,
                        yearlyOvertime: yearlyOvertime.find((el) => el._id === rowData._id),
                        ...getWorkingHours({
                            employeeMonthlyData: findMatchingMonthlyData,
                            employeePreviousMonthData: employeePreviousMonthData, // In case month changed mid-week
                            employeeCategory: rowData.employeeCategory,
                            kathestos: rowData.kathestos,
                            workingDaysPerWeek: rowData.workingDaysPerWeek,
                            workingHoursPerWeek: rowData.workingHoursPerWeek,
                            employeeDataHistory: employeeDataHistory,
                        }),
                    })
                }
            }
            dispatch(setPayrollHourReportDisplayData({
                ...displayData,
                page: 1,
                data: resData.filter((el) => employeeRegex.test(el.employee)),
                unfilteredData: resData,
                totalPages: Math.ceil(resData.length / 31),
                dayAnalysis: dayAnalysis
            }));
        }
        setTimeout(() => {
            setLoading(false);
        }, 50)
    }

    const handleExcelExport = () => {
        let rows = [];
        rows.push(["Επωνυμία:", company.name]);
        rows.push(["ΑΦΜ:", company.vatNumber]);
        rows.push(["ΔΟΥ:", companyDetails.doy]);
        rows.push(["Ημ/νια Εξαγωγής:", moment().format("DD/MM/YYYY HH:mm:ss")]);
        rows.push(["Χρονικό Διάστημα:", `${displayData.date_from} - ${displayData.date_to}`]);
        rows.push([]);

        const header1 = [
            "Στοιχεία Εργαζομένων", "", "", "",
            "Σύνολα", "",
            "Ώρες Εργασίας", "", "", "", "", "", "",
            "Ώρες Αδείας", "", "", "", "", "",
            "Ώρες Υπερεργασίας", "", "", "", "",
            "Ώρες Υπερωρίας", "", "", "", "", "",
        ]
        const header2 = [
            "Α/Α", "Εργαζόμενος", "Τύπος Εργαζομένου", "Μισθός",
            "Ημέρες Εργασίας", "Ώρες εργασίας",
            "ΗΜΕΡΑ", "ΝΥΧΤΑ", "ΗΜΕΡΑ ΚΥΡΙΑΚΗ", "ΝΥΧΤΑ ΚΥΡΙΑΚΗ", "Εκ των οποίων πρόσθετες", "Εκ των οποίων 6ης μέρας", "Εκ των οποίων Αργίες",
            "ΗΜΕΡΑ", "ΝΥΧΤΑ", "ΗΜΕΡΑ ΚΥΡΙΑΚΗ", "ΝΥΧΤΑ ΚΥΡΙΑΚΗ", "Συνολικές Ημέρες", "Συνολικές Ώρες",
            "ΗΜΕΡΑ", "ΝΥΧΤΑ", "ΗΜΕΡΑ ΚΥΡΙΑΚΗ", "ΝΥΧΤΑ ΚΥΡΙΑΚΗ", "Σύνολο",
            "ΗΜΕΡΑ", "ΝΥΧΤΑ", "ΗΜΕΡΑ ΚΥΡΙΑΚΗ", "ΝΥΧΤΑ ΚΥΡΙΑΚΗ", "Σύνολο", "Εκ των οποίων Παράνομες",
        ]
        rows.push(header1);
        rows.push(header2);
        for (let row of displayData.data) {
            rows.push([
                row["no"], row["employee"], getEmployeeCategoryOptions().find((el) => el.value === row["employeeCategory"])?.label + " " + getKathestosOptions().find((el) => el.value === row["kathestos"])?.label, row["salary"],
                row["totals"]["daysWorked"], row["totals"]["hoursWorked"],
                row["working"]["day"], row["working"]["night"], row["working"]["daySunday"], row["working"]["nightSunday"], row["working"]["additionalWork"], row["working"]["sixthDay"], row["working"]["publicHolidays"],
                row["permits"]["day"], row["permits"]["night"], row["permits"]["daySunday"], row["permits"]["nightSunday"], row["permits"]["totalDays"], row["permits"]["totalHours"],
                row["overwork"]["day"], row["overwork"]["night"], row["overwork"]["daySunday"], row["overwork"]["nightSunday"], row["overwork"]["totalHours"],
                row["overtime"]["day"], row["overtime"]["night"], row["overtime"]["daySunday"], row["overtime"]["nightSunday"], row["overtime"]["totalHours"], row["yearlyOvertime"]["illegalOvertime"]
            ])
        }
        const workBook = XLSX.utils.book_new();
        const workSheet = XLSX.utils.aoa_to_sheet(rows);
        XLSX.utils.book_append_sheet(workBook, workSheet, "Ωρομέτρηση");
        XLSX.writeFile(workBook, 'Ωρομέτρηση.xlsx');
    }

    const handlePDFExport = () => {
        const doc = new jsPDF('landscape');
        let y = 23;
        doc.addFileToVFS("arialuni.ttf", liberationSerif);
        doc.addFont("arialuni.ttf", "Arial Unicode MS", "normal");
        doc.addFileToVFS("dejavusans.ttf", dejavuSans);
        doc.addFont("dejavusans.ttf", "DejaVu Sans Normal", "normal");
        doc.addFileToVFS("dejavusansBold.ttf", dejavuSansBold);
        doc.addFont("dejavusansBold.ttf", "DejaVu Sans Bold", "normal");
        doc.setFont("DejaVu Sans Bold");
        doc.setFontSize(12);
        const header = doc.splitTextToSize(`Ωρομέτρηση διαστήματος: ${displayData.date_from} - ${displayData.date_to}`, 200);
        doc.text(header, doc.internal.pageSize.getWidth() / 4, 9, {alignment: "center"});
        doc.setFont("DejaVu Sans Normal");
        doc.setFontSize(9);
        doc.text(`${company.name}, ΑΦΜ: ${company.vatNumber}`, 15, 17, {alignment: "center"});
        doc.text(`Ημερομηνία Εξαγωγής: ${moment().format("DD/MM/YYYY HH:mm:ss")}`, 15, 21, {alignment: "center"});
        const imgData = 'assets/images/prrimer-logo.png';
        const imgX = doc.internal.pageSize.getWidth() - 45; // adjust the position as needed
        const imgY = 3; // adjust the position as needed
        doc.addImage(imgData, 'PNG', imgX, imgY, 40, 15);
        doc.setFont("DejaVu Sans Normal");
        const header1 = [
            {content: "Στοιχεία Εργαζομένων", colSpan: 4},
            {content: "Σύνολα", colSpan: 2},
            {content: "Ώρες Εργασίας", colSpan: 7},
            {content: "Ώρες Αδείας", colSpan: 6},
            {content: "Ώρες Υπερεργασίας", colSpan: 5},
            {content: "Ώρες Υπερωρίας", colSpan: 6},
        ]
        const header2 = [
            "Α/Α", "Εργαζόμενος", "Τύπος Εργ.", "Μισθός",
            "Ημέρες εργ.", "Ώρες εργ.",
            "ΗΜΕΡΑ", "ΝΥΧΤΑ", "ΗΜΕΡΑ ΚΥΡ.", "ΝΥΧΤΑ ΚΥΡ.", "Εκ των οποίων πρόσθετες", "Εκ των οποίων 6ης μέρας", "Εκ των οποίων Αργίες",
            "ΗΜΕΡΑ", "ΝΥΧΤΑ", "ΗΜΕΡΑ ΚΥΡ.", "ΝΥΧΤΑ ΚΥΡ.", "Συν. Ημ.", "Συν. Ώρ.",
            "ΗΜΕΡΑ", "ΝΥΧΤΑ", "ΗΜΕΡΑ ΚΥΡ.", "ΝΥΧΤΑ ΚΥΡ.", "Σύνολο",
            "ΗΜΕΡΑ", "ΝΥΧΤΑ", "ΗΜΕΡΑ ΚΥΡ.", "ΝΥΧΤΑ ΚΥΡ.", "Σύνολο", "Εκ των οποίων Παράνομες",
        ]
        let rows = [];
        for (let row of displayData.data) {
            rows.push([
                row["no"], row["employee"], getEmployeeCategoryOptions().find((el) => el.value === row["employeeCategory"])?.label + "\n" + getKathestosOptions().find((el) => el.value === row["kathestos"])?.label, row["salary"],

                {content: row["totals"]["daysWorked"], styles: {fillColor: "#edfcdc"}},
                {content: row["totals"]["hoursWorked"], styles: {fillColor: "#edfcdc"}},

                {content: row["working"]["day"], styles: {fillColor: "#d4d2fc"}},
                {content: row["working"]["night"], styles: {fillColor: "#ffd6d8"}},
                {content: row["working"]["daySunday"], styles: {fillColor: "#d4d2fc"}},
                {content: row["working"]["nightSunday"], styles: {fillColor: "#ffd6d8"}},
                {content: row["working"]["additionalWork"], styles: {fillColor: "#d4d2fc"}},
                {content: row["working"]["sixthDay"], styles: {fillColor: "#d4d2fc"}},
                {content: row["working"]["publicHolidays"], styles: {fillColor: "#ffcf75"}},

                {content: row["permits"]["day"], styles: {fillColor: "#d4d2fc"}},
                {content: row["permits"]["night"], styles: {fillColor: "#ffd6d8"}},
                {content: row["permits"]["daySunday"], styles: {fillColor: "#d4d2fc"}},
                {content: row["permits"]["nightSunday"], styles: {fillColor: "#ffd6d8"}},
                {content: row["permits"]["totalDays"], styles: {fillColor: "#edfcdc"}},
                {content: row["permits"]["totalHours"], styles: {fillColor: "#edfcdc"}},

                {content: row["overwork"]["day"], styles: {fillColor: "#d4d2fc"}},
                {content: row["overwork"]["night"], styles: {fillColor: "#ffd6d8"}},
                {content: row["overwork"]["daySunday"], styles: {fillColor: "#d4d2fc"}},
                {content: row["overwork"]["nightSunday"], styles: {fillColor: "#ffd6d8"}},
                {content: row["overwork"]["totalHours"], styles: {fillColor: "#edfcdc"}},

                {content: row["overtime"]["day"], styles: {fillColor: "#d4d2fc"}},
                {content: row["overtime"]["night"], styles: {fillColor: "#ffd6d8"}},
                {content: row["overtime"]["daySunday"], styles: {fillColor: "#d4d2fc"}},
                {content: row["overtime"]["nightSunday"], styles: {fillColor: "#ffd6d8"}},
                {content: row["overtime"]["totalHours"], styles: {fillColor: "#edfcdc"}},
                {content: row["yearlyOvertime"]["illegalOvertime"], styles: {fillColor: "#ffcf75"}},
            ])
        }
        autoTable(doc, {
            startY: y,
            head: [header1, header2],
            body: rows,
            rowPageBreak: "avoid",
            styles: {
                font: "DejaVu Sans Normal",
                fontStyle: "normal",
                fontSize: 5, // set font size
                halign: "center", // set alignment
                lineWidth: 0.05,
                lineColor: [181, 181, 181],
            },
            didDrawCell: (data) => { // Draw border around specific columns
                const { x, y, width, height } = data.cell;
                const doc = data.doc;
                doc.setDrawColor(118, 174, 183);
                doc.setLineWidth(0.5);

                if ([0, 4, 6, 13, 19, 24].includes(data.column.index)) {
                    doc.line(x, y - 0.2, x, y + height + 0.2);
                }
                if (data.column.index === 29 || (data.section === "head" && data.column.index === 24 && data.row.index === 0)) {
                    doc.line(x + width, y - 0.2, x + width, y + height + 0.2);
                }

                const lastColIndex = data.table && data.table.columns ? data.table.columns.length - 1 : null;
                const lastBodyRowIndex = data.table && data.table.body ? data.table.body.length - 1 : null;
                const lastHeadRowIndex = data.table && data.table.head ? data.table.head.length - 1 : null;
                const lastFootRowIndex = data.table && data.table.foot ? data.table.foot.length - 1 : null;

                // LEFT outer border (first column)
                if (data.column.index === 0) doc.line(x, y - 0.2, x, y + height + 0.2);

                // RIGHT outer border (last column)
                if (lastColIndex !== null && data.column.index === lastColIndex) {
                    doc.line(x + width, y - 0.2, x + width, y + height + 0.2);
                }

                // TOP outer border:
                // - first header row, OR
                // - first body row when there's no header
                const isFirstHeaderRow = data.section === 'head' && data.row.index === 0;
                const noHeaderAndFirstBodyRow =
                    data.section === 'body' &&
                    data.row.index === 0 &&
                    (!data.table || !data.table.head || data.table.head.length === 0);
                if (isFirstHeaderRow || noHeaderAndFirstBodyRow) {
                    doc.line(x, y, x + width, y);
                }

                // BOTTOM outer border:
                // - last body row, OR
                // - last footer row, OR
                // - if no-body, last header row
                const isLastBodyRow = data.section === 'body' && lastBodyRowIndex !== null && data.row.index === lastBodyRowIndex;
                const isLastFootRow = data.section === 'foot' && lastFootRowIndex !== null && data.row.index === lastFootRowIndex;
                const isLastHeaderNoBody =
                    data.section === 'head' &&
                    (!data.table || !data.table.body || data.table.body.length === 0) &&
                    lastHeadRowIndex !== null &&
                    data.row.index === lastHeadRowIndex;

                if (isLastBodyRow || isLastFootRow || isLastHeaderNoBody) {
                    doc.line(x, y + height, x + width, y + height);
                }
            },
        })
        const pageCount = doc.getNumberOfPages();
        // Add the pagination to each page
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(10);
            doc.text(`Σελίδα ${i} από ${pageCount}`, 10, doc.internal.pageSize.getHeight() - 10);
        }
        doc.save(`Ωρομέτρηση ${displayData.date_from} - ${displayData.date_to}.pdf`);
    }

    const filterEmployees = (e) => {
        const regex = expandSearch(e.target.value);
        const filteredData = displayData.unfilteredData.filter((el) => regex.test(el.employee ?? el.employeeIdentifier));
        dispatch(setPayrollHourReportDisplayData({
            ...displayData,
            page: 1,
            data: filteredData,
            totalPages: Math.ceil(filteredData.length / 31),
        }));
    }

    const thickBorderStyle = "2px solid #76aeb7";

    const aroundStyle = {
        borderTop: thickBorderStyle,
        borderBottom: thickBorderStyle
    }
    const rightStyle = {
        borderRight: thickBorderStyle
    }
    const dayStyle = {
        backgroundColor: "#d4d2fc",
    }
    const nightStyle = {
        backgroundColor: "#ffd6d8",
    }
    const totalStyle = {
        backgroundColor: "#edfcdc",
    }
    const argiesStyle = {
        backgroundColor: "#ffcf75",
    }

    return (
        <div>
            <Row>
                <Col md={12}>
                    <Alert variant={"secondary"}>
                        Πατώντας "Ανάκτηση δεδομένων από το Π.Σ. Εργάνη" θα ανακτηθούν τα δεδομένα ενός εργατικού έτους για τον σωστό υπολογισμό της αναφοράς της ωρομέτρησης. Η διαδικασία μπορεί να διαρκέσει έως και 5 λεπτά.
                        Κατόπιν θα αποθηκευτούν τα δεδομένα στο σύστημα και θα μπορείτε να τα φιλτράρετε με την χρήση των παρακάτω πεδίων.
                        <div style={{textAlign: "center"}}>
                            <Button size={"sm"} onClick={() => fetchReport(true)} disabled={loading} style={{marginTop: "25px"}}>
                                Ανάκτηση δεδομένων από το Π.Σ. Εργάνη
                                {loading && <Spinner className={"ml-2"} animation={"border"} variant={"dark"} />}
                            </Button>
                        </div>
                        {responseData.lastUpdate && (
                            <div style={{textAlign: "center"}}>
                                (*) Τελευταία ανάκτηση: {moment(responseData.lastUpdate).format("DD/MM/YYYY HH:mm")}
                            </div>
                        )}
                    </Alert>
                </Col>
            </Row>
            <Row className={"mb-1"}>
                <div className={"text-muted mb-1"}><i>Φίλτρα</i></div>
                <Col md={2}>
                    <DateHelper
                        dateFromAndToFunction={(from, to) => {
                            dispatch(setPayrollHourReportDisplayData({
                                ...displayData,
                                date_from: from,
                                date_to: to,
                            }))
                            setUpdateDisplayData(true);
                        }}
                    />
                </Col>
                <Col md={2}>
                    <DateBox
                        name={"date_from"}
                        selected={moment(displayData.date_from, "DD/MM/YYYY").isValid() ? moment(displayData.date_from, "DD/MM/YYYY").toDate() : ""}
                        label={"Ημερομηνία από"}
                        onChange={(e) => {
                            if (moment(e).isValid()) {
                                dispatch(setPayrollHourReportDisplayData({
                                    ...displayData,
                                    date_from: moment(e).format("DD/MM/YYYY"),
                                }))
                                setUpdateDisplayData(true);
                            } else {
                                dispatch(setPayrollHourReportDisplayData({
                                    ...displayData,
                                    date_from: "",
                                }))
                                setUpdateDisplayData(true);
                            }
                        }}
                        allowOtherYears={true}
                    />
                </Col>
                <Col md={2}>
                    <DateBox
                        name={"date_to"}
                        selected={moment(displayData.date_to, "DD/MM/YYYY").isValid() ? moment(displayData.date_to, "DD/MM/YYYY").toDate() : ""}
                        label={"Ημερομηνία έως"}
                        onChange={(e) => {
                            if (moment(e).isValid()) {
                                dispatch(setPayrollHourReportDisplayData({
                                    ...displayData,
                                    date_to: moment(e).format("DD/MM/YYYY"),
                                }))
                                setUpdateDisplayData(true);
                            } else {
                                dispatch(setPayrollHourReportDisplayData({
                                    ...displayData,
                                    date_to: "",
                                }))
                                setUpdateDisplayData(true);
                            }
                        }}
                        allowOtherYears={true}
                    />
                </Col>
                <Col md={4}>
                    <Input
                        name={"employeeFilter"}
                        label={"Φιλτράρισμα εργαζόμενου"}
                        autoComplete={"off"}
                        defaultValue={displayData.employeeFilter}
                        onChange={(e) => filterEmployees(e)}
                        onBlur={(e) => dispatch(setPayrollHourReportDisplayData({...displayData, employeeFilter: e.target.value}))}
                    />
                </Col>
                <Col md={2} style={{paddingTop: "32px"}}>
                    <BetterCheckBox
                        name={"dayAnalysis"}
                        text={"Ανάλυση ημερών"}
                        checked={displayData.dayAnalysis}
                        onChange={(e) => setDisplayData(e.target.checked)}
                    />
                </Col>
            </Row>
            <Row className={"mb-2"}>
                <Col md={3}>
                    <Dropdown
                        name={"dataFrom"}
                        label={"Υπολογισμός με βάση"}
                        options={dataFromOptions}
                        defaultValue={dataFromOptions.find((el) => displayData.dataFrom === el.value)}
                        key={displayData.dataFrom}
                        onChange={(e) => {
                            dispatch(setPayrollHourReportDisplayData({
                                ...displayData,
                                dataFrom: e.value,
                            }))
                            setUpdateDisplayData(true);
                        }}
                    />
                </Col>
            </Row>
            {displayData.data.length > 0 && (
                <React.Fragment>
                    <Row>
                        <Col md={12}>
                            <div className="d-flex justify-content-end mb-2">
                                <Button
                                    size={"sm"}
                                    className={"mr-1"}
                                    onClick={() => dispatch(setPayrollHourReportDisplayData({...displayData, page: 1}))}
                                    disabled={displayData.page === 1 || loading}
                                >
                                    {t('SalesBalanceSheet.first')}
                                </Button>
                                <Button
                                    size={"sm"}
                                    className={"mr-1"}
                                    onClick={() => dispatch(setPayrollHourReportDisplayData({
                                        ...displayData,
                                        page: displayData.page - 1
                                    }))}
                                    disabled={displayData.page === 1 || loading}
                                >
                                    {t('SalesBalanceSheet.previous')}
                                </Button>
                                <Badge style={{fontSize: "12px", lineHeight: "20px"}} className="mr-1"
                                       bg="primary">{t('SalesBalanceSheet.page')} {displayData.page} / {displayData.totalPages}</Badge>
                                <Button
                                    size={"sm"}
                                    className={"mr-1"}
                                    onClick={() => dispatch(setPayrollHourReportDisplayData({
                                        ...displayData,
                                        page: displayData.page + 1
                                    }))}
                                    disabled={displayData.page === displayData.totalPages || loading}
                                >
                                    {t('SalesBalanceSheet.next')}
                                </Button>
                                <Button
                                    size={"sm"}
                                    className={"mr-1"}
                                    onClick={() => dispatch(setPayrollHourReportDisplayData({
                                        ...displayData,
                                        page: displayData.totalPages
                                    }))}
                                    disabled={displayData.page === displayData.totalPages || loading}
                                >
                                    {t('SalesBalanceSheet.last')}
                                </Button>
                            </div>
                        </Col>
                    </Row>
                    {!loading && (
                        <React.Fragment>
                            {displayData.dayAnalysis ? (
                                <Row>
                                    <Col md={12}>
                                        <table className={"simpleClassicTable"} style={{fontSize: "16px"}}>
                                            <colgroup>
                                                <col span={1} style={{width: "12.5%"}}></col>
                                                <col span={1} style={{width: "22.5%"}}></col>
                                                <col span={1} style={{width: "10%"}}></col>
                                                <col span={1} style={{width: "10%"}}></col>
                                                <col span={1} style={{width: "12.5%"}}></col>
                                                <col span={1} style={{width: "10%"}}></col>
                                                <col span={1} style={{width: "10%"}}></col>
                                                <col span={1} style={{width: "12.5%"}}></col>
                                            </colgroup>
                                            <thead>
                                            <tr>
                                                <th colSpan={2}></th>
                                                <th colSpan={3}>Πρόγραμμα Εργασίας</th>
                                                <th colSpan={3}>Χτυπήματα Καρτών</th>
                                            </tr>
                                            <tr>
                                                <th>Ημερομηνία</th>
                                                <th>Εργαζόμενος</th>
                                                <th>Ώρα από</th>
                                                <th>Ώρα έως</th>
                                                <th>Ώρες</th>
                                                <th>Ώρα από</th>
                                                <th>Ώρα έως</th>
                                                <th>Ώρες</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {displayData.data.slice((displayData.page - 1) * 31, displayData.page * 31).map((row, idx) => (
                                                <tr key={`phr-${idx}`}>
                                                    <td>{row["date"]}</td>
                                                    <td>{formatEmployeeIdentifier(row["employeeIdentifier"])}</td>
                                                    <td>{row["hourFrom"]}</td>
                                                    <td>{row["hourTo"]}</td>
                                                    <td>{getAmountWorkingHours(row["hourFrom"], row["hourTo"])}</td>
                                                    <td>{row["cardHourFrom"]}</td>
                                                    <td>{row["cardHourTo"]}</td>
                                                    <td>{getAmountWorkingHours(row["cardHourFrom"], row["cardHourTo"])}</td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </Col>
                                </Row>
                            ) : (
                                <React.Fragment>
                                    <Row>
                                        <Col md={12}>
                                            <table className={"simpleClassicTable hourReportTable"} style={{fontSize: "12px", borderLeft: thickBorderStyle, borderRight: thickBorderStyle, wordWrap: "break-word"}}>
                                                <colgroup>
                                                    <col span={1} style={{width: "35px", ...aroundStyle}}></col>
                                                    <col span={1} style={{width: "140px", ...aroundStyle}}></col>
                                                    <col span={2} style={{width: "16%", ...aroundStyle}}></col>

                                                    <col span={2} style={{width: "15%", ...aroundStyle}}></col>

                                                    <col span={7} style={{width: "15%", ...aroundStyle}}></col>

                                                    <col span={6} style={{width: "15%", ...aroundStyle}}></col>

                                                    <col span={5} style={{width: "15%", ...aroundStyle}}></col>

                                                    <col span={6} style={{width: "16.7%", ...aroundStyle}}></col>
                                                </colgroup>
                                                <thead>
                                                <tr>
                                                    <th colSpan={4} style={rightStyle}>Στοιχεία Εργαζομένων</th>
                                                    <th colSpan={2} style={rightStyle}>Σύνολα</th>
                                                    <th colSpan={7} style={rightStyle}>Ώρες Εργασίας</th>
                                                    <th colSpan={6} style={rightStyle}>Ώρες Αδείας</th>
                                                    <th colSpan={5} style={rightStyle}>Ώρες Υπερεργασίας</th>
                                                    <th colSpan={7} style={rightStyle}>Ώρες Υπερωρίας</th>
                                                </tr>
                                                <tr>
                                                    <th>A/A</th>
                                                    <th>Εργαζόμενος</th>
                                                    <th>Τύπος Εργ.</th>
                                                    <th style={rightStyle}>Μισθός</th>

                                                    {/*<th>Ένσημα</th>*/}
                                                    <th>Ημέρες Εργ.</th>
                                                    <th style={rightStyle}>Ώρες Εργ.</th>

                                                    <th>ΗΜΕΡΑ</th>
                                                    <th>ΝΥΧΤΑ</th>
                                                    <th>ΗΜΕΡΑ ΚΥΡ.</th>
                                                    <th>ΝΥΧΤΑ ΚΥΡ.</th>
                                                    <th>Εκ των οποίων πρόσθετες</th>
                                                    <th>Εκ των οποίων 6ης μέρας</th>
                                                    <th style={rightStyle}>Εκ των οποίων Αργίες</th>

                                                    <th>ΗΜΕΡΑ</th>
                                                    <th>ΝΥΧΤΑ</th>
                                                    <th>ΗΜΕΡΑ ΚΥΡ.</th>
                                                    <th>ΝΥΧΤΑ ΚΥΡ.</th>
                                                    <th>Συν. Ημ.</th>
                                                    <th style={rightStyle}>Συν. Ωρ.</th>

                                                    <th>ΗΜΕΡΑ</th>
                                                    <th>ΝΥΧΤΑ</th>
                                                    <th>ΗΜΕΡΑ ΚΥΡ.</th>
                                                    <th>ΝΥΧΤΑ ΚΥΡ.</th>
                                                    <th style={rightStyle}>Σύνολο</th>

                                                    <th>ΗΜΕΡΑ</th>
                                                    <th>ΝΥΧΤΑ</th>
                                                    <th>ΗΜΕΡΑ ΚΥΡ.</th>
                                                    <th>ΝΥΧΤΑ ΚΥΡ.</th>
                                                    <th>Σύνολο</th>
                                                    <th style={{...rightStyle, width: "50px"}}>Εκ των οποίων <span style={{fontSize: "10.5px"}}>Παράνομες</span></th>
                                                </tr>
                                                </thead>
                                                <tbody>
                                                {displayData.data.slice((displayData.page - 1) * 31, displayData.page * 31).map((row, idx) => (
                                                    <tr key={`phr-${idx}`} style={{cursor: "pointer"}} onClick={() => {
                                                        if (row._id) {
                                                            setEmployeeInfoModalId(row._id);
                                                            setShowEmployeeInfoModal(true);
                                                        }
                                                    }}>
                                                        <td>{row["no"]}</td>
                                                        <td>{row["employee"]?.split("|").length !== 0 ? row["employee"].split("|")[0] : ""}</td>
                                                        <td>
                                                            {getEmployeeCategoryOptions().find((el) => el.value === row["employeeCategory"])?.label}
                                                            <br/>
                                                            {getKathestosOptions().find((el) => el.value === row["kathestos"])?.label}
                                                        </td>
                                                        <td style={rightStyle}>{row["salary"]}</td>

                                                        {/*<td style={totalStyle}>{row["totals"]["enshma"]}</td>*/}
                                                        <td style={totalStyle}>{row["totals"]["daysWorked"]}</td>
                                                        <td style={{...rightStyle, ...totalStyle}}>{row["totals"]["hoursWorked"]}</td>

                                                        <td style={dayStyle}>{row["working"]["day"]}</td>
                                                        <td style={nightStyle}>{row["working"]["night"]}</td>
                                                        <td style={dayStyle}>{row["working"]["daySunday"]}</td>
                                                        <td style={nightStyle}>{row["working"]["nightSunday"]}</td>
                                                        <td style={dayStyle}>{row["working"]["additionalWork"]}</td>
                                                        <td style={dayStyle}>{row["working"]["sixthDay"]}</td>
                                                        <td style={{...argiesStyle, ...rightStyle}}>{row["working"]["publicHolidays"]}</td>

                                                        <td style={dayStyle}>{row["permits"]["day"]}</td>
                                                        <td style={nightStyle}>{row["permits"]["night"]}</td>
                                                        <td style={dayStyle}>{row["permits"]["daySunday"]}</td>
                                                        <td style={nightStyle}>{row["permits"]["nightSunday"]}</td>
                                                        <td style={totalStyle}>{row["permits"]["totalDays"]}</td>
                                                        <td style={{...rightStyle, ...totalStyle}}>{row["permits"]["totalHours"]}</td>

                                                        <td style={dayStyle}>{row["overwork"]["day"]}</td>
                                                        <td style={nightStyle}>{row["overwork"]["night"]}</td>
                                                        <td style={dayStyle}>{row["overwork"]["daySunday"]}</td>
                                                        <td style={nightStyle}>{row["overwork"]["nightSunday"]}</td>
                                                        <td style={{...rightStyle, ...totalStyle}}>{row["overwork"]["totalHours"]}</td>

                                                        <td style={dayStyle}>{row["overtime"]["day"]}</td>
                                                        <td style={nightStyle}>{row["overtime"]["night"]}</td>
                                                        <td style={dayStyle}>{row["overtime"]["daySunday"]}</td>
                                                        <td style={nightStyle}>{row["overtime"]["nightSunday"]}</td>
                                                        <td style={totalStyle}>{row["overtime"]["totalHours"]}</td>
                                                        <td style={{...argiesStyle, ...rightStyle}}>{row["yearlyOvertime"]["illegalOvertime"]}</td>
                                                    </tr>
                                                ))}
                                                </tbody>
                                            </table>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <BootstrapDropdown className="mb-3 mt-3">
                                            <BootstrapDropdown.Toggle variant="primary" id="dropdown-basic">
                                                Αποθήκευση αποτελεσμάτων
                                            </BootstrapDropdown.Toggle>
                                            <BootstrapDropdown.Menu>
                                                <BootstrapDropdown.Item onClick={() => handlePDFExport()}>
                                                    Αποθήκευση ως PDF
                                                </BootstrapDropdown.Item>
                                                <BootstrapDropdown.Item onClick={() => handleExcelExport()}>
                                                    Αποθήκευση ως XLSX
                                                </BootstrapDropdown.Item>
                                            </BootstrapDropdown.Menu>
                                        </BootstrapDropdown>
                                    </Row>
                                </React.Fragment>
                            )}
                            <EmployeeInfoModal
                                employeeId={employeeInfoModalId}
                                show={showEmployeeInfoModal}
                                setShow={setShowEmployeeInfoModal}
                            />
                        </React.Fragment>
                    )}
                </React.Fragment>
            )}
        </div>
    )
}

export default PayrollHourReport;
